'use client';

import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { stufe2Stehend } from './rueckmeldung';

/**
 * Current version of the app that is running
 */
let version: string | null = null;

/**
 * Default interval time in seconds to check for new version
 * By default, it is set to 60 seconds
 */
const DEFAULT_REFETCH_INTERVAL = 60;

/**
 * Default interval time in seconds to check for new version
 */
const VERSION_UPDATER_REFETCH_INTERVAL_SECONDS =
  process.env.NEXT_PUBLIC_VERSION_UPDATER_REFETCH_INTERVAL_SECONDS;

const HINWEIS_ID = 'neue-version';

/**
 * Hinweis „Neue Version verfügbar" in der Form der Stufe 2 (`rueckmeldung.tsx`):
 * dieselbe Meldung unten links, mit „Neu laden" als Ausweg.
 *
 * Anders als Stufe 2 bleibt er stehen, bis jemand neu lädt oder ihn schließt
 * (Auftraggeber 13.10.2026): eine neue Version ist ein Zustand, kein Ergebnis —
 * wer nach fünf Sekunden nicht hinsah, arbeitete sonst mit dem alten Stand weiter.
 * Die frühere eigene Karte unten rechts ist gelöscht.
 */
export function VersionUpdater(props: { intervalTimeInSecond?: number }) {
  const { data } = useVersionUpdater(props);
  const { t } = useTranslation('common');

  const neueVersion = data?.didChange ? data.currentVersion : null;

  // Synchronisiert mit dem externen Hinweis-System (sonner); die feste ID
  // verhindert, dass derselbe Hinweis bei jeder Abfrage gestapelt wird.
  useEffect(() => {
    if (!neueVersion) {
      return;
    }

    stufe2Stehend({
      id: HINWEIS_ID,
      text: t('updateAvailable'),
      ausweg: {
        label: t('updateReload'),
        onClick: () => window.location.reload(),
      },
    });
  }, [neueVersion, t]);

  return null;
}

function useVersionUpdater(props: { intervalTimeInSecond?: number } = {}) {
  const interval = VERSION_UPDATER_REFETCH_INTERVAL_SECONDS
    ? Number(VERSION_UPDATER_REFETCH_INTERVAL_SECONDS)
    : DEFAULT_REFETCH_INTERVAL;

  const refetchInterval = (props.intervalTimeInSecond ?? interval) * 1000;

  // start fetching new version after half of the interval time
  const staleTime = refetchInterval / 2;

  return useQuery({
    queryKey: ['version-updater'],
    staleTime,
    gcTime: refetchInterval,
    refetchIntervalInBackground: true,
    refetchInterval,
    initialData: null,
    queryFn: async () => {
      const url = new URL('/api/version', process.env.NEXT_PUBLIC_SITE_URL);
      const response = await fetch(url.toString());

      const currentVersion = await response.text();
      const oldVersion = version;

      version = currentVersion;

      const didChange = oldVersion !== null && currentVersion !== oldVersion;

      return {
        currentVersion,
        oldVersion,
        didChange,
      };
    },
  });
}
